import { useState, useEffect } from "react";
import { supabase, MUTED, TEXT, ROW_LINE, pageTitle, cardFlush, text, linkBtn, LINE, INK, NEUTRAL_BG, HOVER_BG, FAINT, CULTURE_COLORS } from "./constants";
import { CandidateAvatar, evalItemIds, evalAvg5, SelectRow, candidatePhase, FunnelArrows } from "./shared";
import { ChevronDown, ChevronRight } from "lucide-react";
import CultureProfile, { hasCultureProfile } from "./CultureProfile";
import { cleanAnswers, cultureScores, cultureFit, CULTURE_DIMS } from "./lib/culture";
import { useIsMobile } from "./lib/responsive";

// ─── HOME ─────────────────────────────────────────────────────────────────────
// Responde a una pregunta: qué tengo que hacer hoy y cómo va mi búsqueda.
// 02/11: tres secciones, en el orden de lo que se pregunta un founder al entrar.
//   (03/11: «Lo que toca» salió de la Home: es la campanita del menú,
//   Notificaciones, con la burbuja roja; ver lib/todos.js.)
//   POSICIONES: Abiertas | Embudo (el de flechas de Posiciones).
//   CANDIDATOS: Top candidatos (los 3 mejores de una posición) | Lo último (3).
// Escritorio: la sección lleva su nombre fuera y cada parte su título dentro;
// las dos partes de una sección, mitades iguales y a la misma altura.
// El Culture statement hecho ya no está aquí: se consulta desde el Brief de
// cada posición y desde su página. Las 4 cifras de antes repetían el embudo.
//   CULTURA DE LA EMPRESA: Mapa de cultura (hexágono) | Fit (qué candidato buscar).
// Móvil: una columna.
// Regla: lo pulsable va en azul o con la mano; las cifras que no llevan a
// ningún sitio, en negro.

// Fase de un candidato: la pieza común de shared.jsx (misma lógica que el Tablero)
const phaseOf = candidatePhase;

// Nota de cualificación sobre 5 (la misma que "Cual." del Tablero)
const qualAvg = (c) => {
  const v = c.scoring ? Object.values(c.scoring) : [];
  return v.some(x => x > 0) ? v.reduce((s, x) => s + x, 0) / v.length : null;
};

const DashboardView = ({ t, lang, setView, setSelectedPosition, goToCandidate, openPositionsList = [], closedPositionsList = [], isAdmin = false, selectedClientId = null, hasCultureDoc = false, photoUrls = {}, candidateStatuses = {} }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const [topPos, setTopPos] = useState(null);       // posición elegida en "Top candidatos"
  const [funnelPos, setFunnelPos] = useState("all"); // embudo: "all" o el id de una posición (31/10)
  const [allLatest, setAllLatest] = useState(false); // "Lo último": 5 o todo
  const [folded, setFolded] = useState({ funnel: false, latest: false }); // móvil: true = desplegado
  const [cultureAnswers, setCultureAnswers] = useState({}); // respuestas del cuestionario de cultura (sección Empresa)

  useEffect(() => {
    setCultureAnswers({});
    if (!selectedClientId || !hasCultureDoc) return;
    supabase.from("culture_docs").select("answers").eq("client_id", selectedClientId).maybeSingle()
      .then(({ data }) => setCultureAnswers(cleanAnswers(data?.answers)));
  }, [selectedClientId, hasCultureDoc]);

  const pairs = openPositionsList.flatMap(p => (p.candidates || []).map(c => ({ c, p })));
  const allCands = pairs.map(x => x.c);


  // ── Cifras ──
  const phaseCount = (list) => {
    const out = { pending: 0, validated: 0, interview: 0, references: 0, rejected: 0 };
    list.forEach(c => { out[phaseOf(c, candidateStatuses)]++; });
    return out;
  };

  // ── Lo último: un solo feed con fecha real ──
  const feed = [];
  pairs.forEach(({ c, p }) => {
    if (c.qualifiedAt) feed.push({ at: c.qualifiedAt, c, p, text: es ? <>cualificado/a para <strong>{p.title}</strong></> : <>qualified for <strong>{p.title}</strong></> });
    if (c.validatedAt) feed.push({ at: c.validatedAt, c, p, text: es ? <>validado/a para <strong>{p.title}</strong></> : <>validated for <strong>{p.title}</strong></> });
    if (c.referencesReadyAt) feed.push({ at: c.referencesReadyAt, c, p, tab: "references", text: es ? <>tiene las referencias listas</> : <>has references ready</> });
    if (c.evalUpdatedAt && (c.evalManagers || []).length) {
      const who = c.evalManagers.map(m => m.name).filter(Boolean).slice(0, 2).join(", ");
      feed.push({ at: c.evalUpdatedAt, c, p, tab: "evaluation", text: es ? <>evaluado/a{who ? <> por <strong>{who}</strong></> : ""}</> : <>evaluated{who ? <> by <strong>{who}</strong></> : ""}</> });
    }
  });
  feed.sort((a, b) => new Date(b.at) - new Date(a.at));
  // Escritorio: 3, a la altura de «Top candidatos», que va al lado
  const latestN = isMobile ? 5 : 3;
  const latest = allLatest ? feed : feed.slice(0, latestN);
  const ago = (iso) => {
    const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
    if (es) return days === 0 ? "Hoy" : days === 1 ? "Ayer" : days < 7 ? `Hace ${days} días` : days < 30 ? `Hace ${Math.floor(days / 7)} sem.` : `Hace ${Math.floor(days / 30)} mes.`;
    return days === 0 ? "Today" : days === 1 ? "Yesterday" : days < 7 ? `${days} days ago` : days < 30 ? `${Math.floor(days / 7)} wk ago` : `${Math.floor(days / 30)} mo ago`;
  };

  // ── Top candidatos: los 3 mejores de una posición ──
  // Nota: la evaluación del equipo si la hay (es la más informada); si no, la
  // de cualificación. Sin ninguna de las dos, no entra. Fuera los descartados.
  const scored = (p) => {
    const ids = evalItemIds(lang, p.evalQuestions);
    return (p.candidates || [])
      .filter(c => phaseOf(c, candidateStatuses) !== "rejected")
      .map(c => {
        const ev = evalAvg5(c.evalManagers, c.evalScores, ids);
        const q = qualAvg(c);
        return ev != null ? { c, score: ev, kind: "eval" } : q != null ? { c, score: q, kind: "qual" } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };
  const topP = openPositionsList.find(p => p.id === topPos) || openPositionsList[0] || null;
  const top = topP ? scored(topP) : [];

  // ── Estilos ──
  const card = { ...cardFlush, overflow: "visible" };
  const pad = isMobile ? 16 : 20;
  const cardHead = (title, right) => (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, padding: `16px ${pad}px 12px` }}>
      <span style={text.cardTitle}>{title}</span>
      {right}
    </div>
  );
  // Móvil: cabecera que despliega un bloque secundario (Embudo, Lo último)
  const foldHead = (key, title) => (
    <button type="button" onClick={() => setFolded(f => ({ ...f, [key]: !f[key] }))} aria-expanded={folded[key]}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minHeight: 48, padding: `0 ${pad}px`, background: "none", border: "none", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
      <span style={text.cardTitle}>{title}</span>
      <ChevronDown size={18} strokeWidth={1.75} color={MUTED} style={{ transform: folded[key] ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
    </button>
  );
  const phaseLabel = { pending: es ? "Pendientes" : "Pending", validated: es ? "Validados" : "Validated", interview: es ? "Entrevista" : "Interview", references: es ? "Referencias" : "References" };
  const avatar = (c, size = 30) => photoUrls[c.id]
    ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block", flexShrink: 0, boxSizing: "border-box" }} />
    : <CandidateAvatar name={c.name} size={size} />;
  // Abrir una posición (opcionalmente en una pestaña). La pestaña se recuerda
  // en localStorage, como hace la propia posición.
  // `focus` = "pending": entra en Candidatos y el número rojo de Pendientes
  // echa dos ondas (lo lee CandidatesView una sola vez).
  const openPos = (p, tab, focus) => {
    if (tab) { try { localStorage.setItem("rutment_position_tab", JSON.stringify({ id: p.id, tab })); } catch (e) { /* sin almacenamiento */ } }
    if (focus) { try { sessionStorage.setItem("rutment_focus", JSON.stringify({ id: p.id, what: focus })); } catch (e) { /* sin almacenamiento */ } }
    setSelectedPosition(p); setView("candidates");
  };
  // Embudo: quien ha llegado al menos a cada fase (fuera los descartados)
  // Con varias posiciones abiertas se elige cuál (31/10): todas o una
  const funnelP = openPositionsList.find(p => String(p.id) === String(funnelPos)) || null;
  const fCands = funnelP ? (funnelP.candidates || []) : allCands;
  const fTotals = phaseCount(fCands);
  const atLeast = (list) => fCands.filter(c => list.includes(phaseOf(c, candidateStatuses))).length;
  const stages = [
    { key: "candidates", label: es ? "Candidatos" : "Candidates", n: fCands.length },
    { key: "validated", label: phaseLabel.validated, n: atLeast(["validated", "interview", "references"]) },
    { key: "interview", label: phaseLabel.interview, n: atLeast(["interview", "references"]) },
    { key: "references", label: phaseLabel.references, n: atLeast(["references"]) },
  ];
  // Escritorio: todo en dos mitades iguales, para que los bordes cuadren de arriba abajo
  const HALVES = "minmax(0, 1fr) minmax(0, 1fr)";
  // Un bloque con su nombre encima. La tarjeta estira hasta la altura de la de al lado
  // `extra` (31/10): junto al nombre, p. ej. el «?»; con alto fijo para
  // que la caja no baje respecto a la de al lado
  // `right` (02/11): el enlace del bloque, a la derecha de la etiqueta
  const section = (title, node, extra, right) => (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ ...text.label, margin: "0 2px 8px 2px", height: 14, display: "flex", alignItems: "center", gap: 2 }}>
        {title}
        {/* Pegado al nombre, como el «?» de las pestañas de la posición (31/10) */}
        {extra && <span style={{ display: "inline-flex", margin: "-9px 0" }}>{extra}</span>}
        {right && <span style={{ marginLeft: "auto", display: "inline-flex", textTransform: "none", letterSpacing: 0 }}>{right}</span>}
      </div>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", minWidth: 0 }}>{node}</div>
    </div>
  );
  const fmtScore = (n) => n.toFixed(1);

  // Filas pulsables enteras (31/10): el texto va en negro y lo pulsable lo dicen
  // la fila que se marca al pasar el ratón y la flecha. El azul queda para los
  // enlaces sueltos (Ver todo, Ver, Rehacer) y los botones.
  const rowHover = isMobile ? {} : {
    onMouseEnter: e => { e.currentTarget.style.background = HOVER_BG; },
    onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
  };
  const rowArrow = <ChevronRight size={16} strokeWidth={1.75} color={FAINT} style={{ flexShrink: 0 }} />;
  const empty = (msg) => <div style={{ padding: `0 ${pad}px 18px`, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>{msg}</div>;

  // Bloques de la Home: el escritorio los reparte en dos columnas; el móvil,
  // en una, con lo urgente arriba y lo secundario plegado al final (24/10).
  const topCard = (
    <div style={{ ...card, overflow: isMobile ? "hidden" : "visible" }}>{/* visible: el desplegable de Posición no se recorta */}
      {cardHead(es ? "Top candidatos" : "Top candidates")}
      {/* Con varias posiciones, se elige en una fila (la misma en escritorio y móvil, 24/10) */}
      {openPositionsList.length > 1 && (
        <SelectRow label={es ? "Posición" : "Position"} display={topP ? topP.title : ""} value={topP ? String(topP.id) : ""}
          options={openPositionsList.map(p => [String(p.id), p.title])}
          onChange={v => { const p = openPositionsList.find(x => String(x.id) === v); if (p) setTopPos(p.id); }} />
      )}
      {!topP ? empty(es ? "No hay posiciones abiertas." : "No open positions.")
        : top.length === 0 ? empty(es ? "Aún no hay candidatos puntuados en esta posición." : "No scored candidates in this position yet.")
        : top.map(({ c, score, kind }, i) => (
          <div key={c.id} role="button" tabIndex={0} onClick={() => goToCandidate(c, topP, kind === "eval" ? "evaluation" : undefined, "dashboard")}
            onKeyDown={e => { if (e.key === "Enter") goToCandidate(c, topP, kind === "eval" ? "evaluation" : undefined, "dashboard"); }}
            onMouseEnter={isMobile ? undefined : (e => { e.currentTarget.style.background = HOVER_BG; })}
            onMouseLeave={isMobile ? undefined : (e => { e.currentTarget.style.background = "transparent"; })}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: `10px ${pad}px`, borderTop: `1px solid ${ROW_LINE}`, cursor: "pointer", minHeight: 44, boxSizing: "border-box" }}>
            <span style={{ width: 14, fontSize: 12, fontWeight: 700, color: MUTED, flexShrink: 0, textAlign: "center" }}>{i + 1}</span>
            {avatar(c, 32)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
              <div style={{ fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[c.position, c.company].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums" }}>{fmtScore(score)}<span style={{ fontSize: 11, color: MUTED, fontWeight: 600 }}>/5</span></div>
              <div style={{ fontSize: 11, color: MUTED }}>{kind === "eval" ? (es ? "Evaluación" : "Evaluation") : (es ? "Cualificación" : "Qualification")}</div>
            </div>
            {rowArrow}
          </div>
        ))}
    </div>
  );
  const seeAll = feed.length > latestN && (
    <button onClick={() => setAllLatest(v => !v)} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40 } : null) }}>
      {allLatest ? (es ? "Ver menos" : "Show less") : (es ? "Ver todo" : "See all")}
    </button>
  );
  const latestCard = (
    <div style={{ ...card, overflow: "hidden" }}>
      {/* Móvil: la cabecera pliega y despliega siempre (31/10: abierta ya no se podía cerrar); Ver todo va debajo */}
      {isMobile ? foldHead("latest", es ? "Lo último" : "Latest") : cardHead(es ? "Lo último" : "Latest", seeAll)}
      {isMobile && !folded.latest ? null : latest.length === 0 ? empty(es ? "Todavía no hay movimientos con fecha. Aparecerán aquí cuando se cualifique, valide o evalúe a alguien." : "No dated activity yet. It will appear here when someone is qualified, validated or evaluated.")
        : latest.map((it, i) => (
        // En el móvil toda la fila abre la ficha (el nombre solo es un blanco demasiado pequeño)
        <div key={i} role="button" tabIndex={0} onClick={() => goToCandidate(it.c, it.p, it.tab, "dashboard")} onKeyDown={e => { if (e.key === "Enter") goToCandidate(it.c, it.p, it.tab, "dashboard"); }}
          {...rowHover}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: `10px ${pad}px`, borderTop: `1px solid ${ROW_LINE}`, minWidth: 0, minHeight: isMobile ? 44 : undefined, cursor: "pointer" }}>
          {avatar(it.c)}
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: TEXT, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
            <span style={{ fontWeight: 700, color: INK }}>{it.c.name}</span>
            {" "}{it.text}
          </div>
          <span style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>{ago(it.at)}</span>
        </div>
      ))}
      {isMobile && folded.latest && seeAll && <div style={{ padding: `4px ${pad}px 8px`, borderTop: `1px solid ${ROW_LINE}` }}>{seeAll}</div>}
    </div>
  );
  // Posiciones abiertas (02/11): cada una, una fila que la abre
  const allPositions = <button onClick={() => setView("positions")} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40 } : null) }}>{es ? "Ver todas" : "See all"}</button>;
  const positionsCard = (
    <div style={{ ...card, overflow: "hidden" }}>
      {cardHead(isMobile ? (es ? "Posiciones abiertas" : "Open positions") : (es ? "Abiertas" : "Open"), allPositions)}
      {openPositionsList.length === 0 ? empty(es ? "No hay posiciones abiertas." : "No open positions.")
        : openPositionsList.map((p, i) => {
          const n = (p.candidates || []).length;
          const pend = (p.candidates || []).filter(c => phaseOf(c, candidateStatuses) === "pending").length;
          return (
            <div key={p.id} role="button" tabIndex={0} onClick={() => openPos(p)} onKeyDown={e => { if (e.key === "Enter") openPos(p); }} {...rowHover}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: `11px ${pad}px`, borderTop: `1px solid ${ROW_LINE}`, cursor: "pointer", minHeight: 44, boxSizing: "border-box" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: isMobile ? 15 : 13, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                <div style={{ fontSize: 12, color: MUTED }}>
                  {n} {es ? (n === 1 ? "candidato" : "candidatos") : (n === 1 ? "candidate" : "candidates")}{pend > 0 && ` · ${pend} ${es ? "por revisar" : "to review"}`}
                </div>
              </div>
              {rowArrow}
            </div>
          );
        })}
    </div>
  );
  // Embudo: las filas, solas. Van en su tarjeta (móvil) o en el bloque Posiciones (escritorio)
  // 31/10: bloques en flecha como el embudo de Posiciones, en gris y sin más
  // colores. Cada cifra cuenta a quien ha llegado AL MENOS a esa fase (así
  // siempre baja) y debajo, el % que pasa de la anterior.
  // Móvil: las flechas no caben; el mismo embudo en vertical, una barra
  // centrada por fase que se estrecha con la cifra
  const stageMax = Math.max(1, ...stages.map(s => s.n));
  // Siempre que haya alguna posición: «Todas» ya es una opción en sí (no como Top candidatos)
  const funnelPick = openPositionsList.length > 0 && (
    <SelectRow label={es ? "Posición" : "Position"} display={funnelP ? funnelP.title : (es ? "Todas las posiciones" : "All positions")} value={funnelP ? String(funnelP.id) : "all"}
      options={[["all", es ? "Todas las posiciones" : "All positions"], ...openPositionsList.map(p => [String(p.id), p.title])]}
      onChange={v => setFunnelPos(v)} />
  );
  // Móvil: el embudo en barras que se estrechan (las flechas no caben)
  const funnelRows = (
    <div style={{ padding: `0 ${pad}px 4px` }}>
      {stages.map((f, i) => {
        const prev = stages[i - 1];
        return (
          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ width: 84, fontSize: 12, color: TEXT, flexShrink: 0 }}>{f.label}</span>
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <div style={{ width: `${Math.max(18, (f.n / stageMax) * 100)}%`, background: NEUTRAL_BG, borderRadius: 6, padding: "6px 0", textAlign: "center", fontSize: 15, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums" }}>{f.n}</div>
            </div>
            <span style={{ width: 34, fontSize: 11, color: MUTED, textAlign: "right", flexShrink: 0 }}>{i === 0 ? "" : prev.n ? `${Math.round((f.n / prev.n) * 100)}%` : "–"}</span>
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: MUTED, margin: "8px 0 4px" }}>
        {fTotals.pending} {es ? "por revisar" : "to review"}{fTotals.rejected > 0 && <> · {fTotals.rejected} {es ? (fTotals.rejected === 1 ? "descartado" : "descartados") : "rejected"}</>}
      </div>
    </div>
  );
  const funnelCard = (
    <div style={{ ...card, padding: isMobile && !folded.funnel ? 0 : "0 0 16px", overflow: "hidden" }}>
      {isMobile ? foldHead("funnel", es ? "Embudo" : "Funnel") : cardHead(es ? "Embudo" : "Funnel")}
      {(!isMobile || folded.funnel) && <>{funnelPick}{funnelRows}</>}
    </div>
  );
  // ── Empresa (03/11): abajo del todo, el Culture statement. Hexágono a la
  // izquierda y, a la derecha, «Fit»: qué candidato encaja en cada dimensión
  // según la nota (cultureFit de lib/culture.js), para saber de un vistazo
  // qué buscar. Mismas mitades que las demás secciones. Sin hacer, «Empezar».
  const hasProfile = hasCultureProfile(cultureAnswers);
  const cScores = cultureScores(cultureAnswers);
  const cultureLink = (
    <button onClick={() => setView(hasCultureDoc ? "documents" : "cultureQuestionnaire")} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40 } : null) }}>
      {hasCultureDoc ? (es ? "Ver" : "View") : (es ? "Empezar →" : "Start →")}
    </button>
  );
  // 03/11: dos partes, como las demás secciones: «Culture map» (el hexágono,
  // con Ver) y «Fit» (qué candidato encaja en cada dimensión), misma cabecera
  // y misma altura. Sin perfil, solo la primera con su línea.
  const mapCard = (
    <div style={{ ...card, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {cardHead(es ? "Mapa de cultura" : "Culture map", cultureLink)}
      {hasProfile ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: `0 ${pad}px ${isMobile ? 8 : 16}px` }}>
          <CultureProfile answers={cultureAnswers} lang={lang} radar title={false} style={{ width: "100%", maxWidth: 340 }} />
        </div>
      ) : empty(hasCultureDoc
        ? (es ? "Se comparte con cada candidato antes de la entrevista." : "Shared with every candidate before the interview.")
        : (es ? "14 preguntas, 10-12 minutos. Es lo que permite juzgar el encaje cultural." : "14 questions, 10-12 minutes. It's what makes cultural fit judgeable."))}
    </div>
  );
  const fitCard = hasProfile && (
    <div style={{ ...card, overflow: "hidden" }}>
      {cardHead("Fit")}
      {CULTURE_DIMS.filter(d => cScores[d.id] != null).map(d => (
        <div key={d.id} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: `10px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: CULTURE_COLORS[d.id], flexShrink: 0, transform: "translateY(-1px)" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{es ? d.es : d.en}</div>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{cultureFit(d.id, cScores[d.id], lang)}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      {/* "Abrir nueva posición" vive solo en Posiciones (decisión de Borja, 13/10) */}
      {/* Mismas medidas que la cabecera del portal (Page de Layout.jsx): h1, 4 px y 32 / 20 debajo */}
      <div style={{ marginBottom: isMobile ? 20 : 32 }}>
        <h1 style={pageTitle(isMobile, 4)}>{t.dashboard}</h1>
      </div>

      {/* Móvil: una columna; el embudo y Lo último, plegados */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {positionsCard}
          {funnelCard}
          {topCard}
          {latestCard}
          {selectedClientId && mapCard}
          {selectedClientId && fitCard}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {section(es ? "Posiciones" : "Positions", (
            <div style={{ display: "grid", gridTemplateColumns: HALVES, gap: 16, alignItems: "stretch" }}>
              {positionsCard}
              <div style={{ ...card, overflow: "visible", paddingBottom: 16 }}>{/* visible: la lista del selector no se recorta */}
                {cardHead(es ? "Embudo" : "Funnel")}
                {funnelPick}
                {/* El mismo embudo de flechas que Posiciones (pieza común) */}
                <div style={{ padding: `14px ${pad}px 0` }}>
                  <FunnelArrows stages={stages} lang={lang} compact />
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>
                    {fTotals.pending} {es ? "por revisar" : "to review"}{fTotals.rejected > 0 && <> · {fTotals.rejected} {es ? (fTotals.rejected === 1 ? "descartado" : "descartados") : "rejected"}</>}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {section(es ? "Candidatos" : "Candidates", (
            <div style={{ display: "grid", gridTemplateColumns: HALVES, gap: 16, alignItems: "stretch" }}>
              {topCard}
              {latestCard}
            </div>
          ))}
          {selectedClientId && section(es ? "Cultura de la empresa" : "Company culture", (
            <div style={{ display: "grid", gridTemplateColumns: fitCard ? HALVES : "minmax(0, 1fr)", gap: 16, alignItems: "stretch" }}>
              {mapCard}
              {fitCard}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardView;
